import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { 
  Zap, TrendingUp, Sparkles, Users, ArrowRight, 
  Eye, Gift, Shield, BarChart3, Wand2,
  CheckCircle, Trophy, Share2
} from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const LandingPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [yearlyBilling, setYearlyBilling] = useState(false);
  const [promoVideoUrl, setPromoVideoUrl] = useState('https://www.youtube.com/embed/zLH-Vlav-5U?rel=0&modestbranding=1');
  
  // Get referral code from URL if present
  const refCode = searchParams.get('ref');
  
  useEffect(() => {
    // Fetch site settings for promo video (public endpoint)
    const fetchSettings = async () => {
      try {
        const response = await axios.get(`${API}/api/site-settings`);
        if (response.data?.promo_video_url) {
          let url = response.data.promo_video_url;
          let videoId = null;
          
          // Only process YouTube URLs
          if (url.includes('youtube.com/watch?v=')) {
            videoId = url.split('v=')[1]?.split('&')[0];
          } else if (url.includes('youtu.be/')) {
            videoId = url.split('youtu.be/')[1]?.split('?')[0];
          } else if (url.includes('youtube.com/embed/')) {
            videoId = url.split('embed/')[1]?.split('?')[0];
          }
          
          // Only update if we got a valid YouTube video ID
          if (videoId) {
            setPromoVideoUrl(`https://www.youtube.com/embed/${videoId}?rel=0&modestbranding=1`);
          }
          // If not a YouTube URL, keep the default
        }
      } catch (error) {
        // Use default video if settings not found
        console.log('Using default promo video');
      }
    };
    fetchSettings();
  }, []);
  
  const getPrice = (monthly) => {
    if (yearlyBilling) {
      // 2 months free = 10 months price / 12
      return Math.round(monthly * 10 / 12);
    }
    return monthly;
  };

  return (
    <div className="min-h-screen bg-void">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 glass">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 bg-neon-mint rounded-lg flex items-center justify-center">
              <Zap className="w-6 h-6 text-black" />
            </div>
            <span className="font-heading font-bold text-xl text-white">TrafficFlow</span>
            <span className="font-heading font-bold text-xl text-neon-violet">AI</span>
          </div>
          <nav className="hidden md:flex items-center gap-8">
            <a href="#features" className="text-gray-400 hover:text-white transition-colors">Features</a>
            <a href="#how-it-works" className="text-gray-400 hover:text-white transition-colors">How It Works</a>
            <a href="#pricing" className="text-gray-400 hover:text-white transition-colors">Pricing</a>
          </nav>
          <div className="flex items-center gap-4">
            <Link to={refCode ? `/auth?ref=${refCode}` : "/auth"}>
              <Button variant="ghost" className="text-gray-300 hover:text-white" data-testid="login-btn">
                Log In
              </Button>
            </Link>
            <Link to={refCode ? `/auth?mode=register&ref=${refCode}` : "/auth?mode=register"}>
              <Button className="bg-neon-mint text-black hover:bg-neon-mint/90 font-bold shadow-neon btn-press" data-testid="get-started-btn">
                Get Started Free
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6 relative overflow-hidden">
        <div 
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: `url(https://images.unsplash.com/photo-1710957987034-cea509422852?crop=entropy&cs=srgb&fm=jpg&q=85)`,
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-void via-void/95 to-void" />
        
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 mb-8">
              <Sparkles className="w-4 h-4 text-neon-violet" />
              <span className="text-sm text-gray-300">AI-Powered Traffic Exchange Platform</span>
            </div>
            
            <h1 className="font-heading font-extrabold text-4xl sm:text-5xl lg:text-6xl text-white mb-6 leading-tight">
              Get Real Traffic.<br />
              <span className="gradient-text">Earn While You Browse.</span>
            </h1>
            
            <p className="text-gray-400 text-lg mb-10 max-w-2xl mx-auto">
              Join thousands of entrepreneurs and marketers exchanging traffic. 
              Earn credits by viewing, spend credits to promote. Supercharge with AI.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/auth?mode=register">
                <Button 
                  size="lg" 
                  className="bg-neon-mint text-black hover:bg-neon-mint/90 font-bold text-lg px-8 shadow-neon-lg btn-press"
                  data-testid="hero-cta-btn"
                >
                  Start Earning Credits <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <a href="#video">
                <Button 
                  size="lg" 
                  variant="outline" 
                  className="border-white/20 text-white hover:bg-white/5"
                >
                  <Eye className="mr-2 w-5 h-5" />
                  Watch How It Works
                </Button>
              </a>
            </div>
            
            <div className="mt-16 grid grid-cols-3 gap-8 max-w-xl mx-auto">
              <div className="text-center">
                <p className="font-mono font-bold text-3xl text-neon-mint">10K+</p>
                <p className="text-gray-500 text-sm">Active Users</p>
              </div>
              <div className="text-center">
                <p className="font-mono font-bold text-3xl text-neon-mint">1M+</p>
                <p className="text-gray-500 text-sm">Views Exchanged</p>
              </div>
              <div className="text-center">
                <p className="font-mono font-bold text-3xl text-neon-mint">50K+</p>
                <p className="text-gray-500 text-sm">Clicks Generated</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Video Section */}
      <section id="video" className="py-20 px-6 bg-void-paper">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-heading font-bold text-3xl sm:text-4xl text-white mb-4">
              See <span className="text-neon-violet">TrafficFlow AI</span> in Action
            </h2>
            <p className="text-gray-400">Watch how entrepreneurs are growing their traffic with our platform</p>
          </div>
          
          {/* Embedded Video */}
          <div className="relative aspect-video rounded-2xl overflow-hidden bg-void border border-white/10">
            <iframe
              src={promoVideoUrl}
              title="TrafficFlow AI Demo"
              className="absolute inset-0 w-full h-full"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            ></iframe>
            
            {/* Overlay for non-playing state */}
            <div className="absolute inset-0 bg-gradient-to-br from-neon-mint/10 via-transparent to-neon-violet/10 pointer-events-none" />
          </div>
          
          {/* Key Points */}
          <div className="grid md:grid-cols-3 gap-6 mt-8">
            <div className="flex items-start gap-3 p-4 bg-void rounded-xl border border-white/5">
              <div className="w-10 h-10 rounded-lg bg-neon-mint/10 flex items-center justify-center flex-shrink-0">
                <Eye className="w-5 h-5 text-neon-mint" />
              </div>
              <div>
                <h4 className="text-white font-medium mb-1">View & Earn</h4>
                <p className="text-gray-500 text-sm">Watch promotions for 10 seconds to earn credits</p>
              </div>
            </div>
            <div className="flex items-start gap-3 p-4 bg-void rounded-xl border border-white/5">
              <div className="w-10 h-10 rounded-lg bg-neon-violet/10 flex items-center justify-center flex-shrink-0">
                <Wand2 className="w-5 h-5 text-neon-violet" />
              </div>
              <div>
                <h4 className="text-white font-medium mb-1">AI-Powered Copy</h4>
                <p className="text-gray-500 text-sm">Generate compelling headlines with GPT-5.2</p>
              </div>
            </div>
            <div className="flex items-start gap-3 p-4 bg-void rounded-xl border border-white/5">
              <div className="w-10 h-10 rounded-lg bg-neon-amber/10 flex items-center justify-center flex-shrink-0">
                <Share2 className="w-5 h-5 text-neon-amber" />
              </div>
              <div>
                <h4 className="text-white font-medium mb-1">Refer & Earn More</h4>
                <p className="text-gray-500 text-sm">Get 50 credits for every friend you refer</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-heading font-bold text-3xl sm:text-4xl text-white mb-4">
              Everything You Need to <span className="text-neon-mint">Grow</span>
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              A complete traffic exchange platform with AI-powered tools to maximize your results.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: Eye, title: 'Earn by Viewing', desc: 'Watch promotions for a few seconds and earn credits instantly.', color: 'mint' },
              { icon: TrendingUp, title: 'Promote Your Links', desc: 'Spend credits to display your promotions in the feed.', color: 'mint' },
              { icon: Gift, title: 'Daily Bonuses', desc: 'Log in daily to claim free bonus credits.', color: 'amber' },
              { icon: Wand2, title: 'AI Copy Generator', desc: 'Generate compelling headlines and descriptions with AI.', color: 'violet' },
              { icon: Trophy, title: 'Leaderboard & Referrals', desc: 'Climb the ranks and earn by referring friends.', color: 'amber' },
              { icon: Shield, title: 'Anti-Abuse System', desc: 'Fair play rules ensure quality traffic for everyone.', color: 'mint' },
            ].map((feature, i) => (
              <div 
                key={i}
                className="bg-void-paper border border-white/10 rounded-xl p-6 card-hover group"
              >
                <div className={`w-12 h-12 rounded-lg flex items-center justify-center mb-4 ${
                  feature.color === 'mint' ? 'bg-neon-mint/10' : 
                  feature.color === 'violet' ? 'bg-neon-violet/10' : 'bg-neon-amber/10'
                }`}>
                  <feature.icon className={`w-6 h-6 ${
                    feature.color === 'mint' ? 'text-neon-mint' : 
                    feature.color === 'violet' ? 'text-neon-violet' : 'text-neon-amber'
                  }`} />
                </div>
                <h3 className="font-heading font-semibold text-lg text-white mb-2">{feature.title}</h3>
                <p className="text-gray-400 text-sm">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="py-20 px-6 bg-void-paper">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-heading font-bold text-3xl sm:text-4xl text-white mb-4">
              How <span className="text-neon-violet">TrafficFlow AI</span> Works
            </h2>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { step: '01', title: 'Sign Up Free', desc: 'Create your account in seconds and get 50 welcome credits.', icon: Users },
              { step: '02', title: 'Earn Credits', desc: 'View other promotions to earn credits. Complete the timer to collect.', icon: Eye },
              { step: '03', title: 'Promote & Grow', desc: 'Spend credits to display your links. Real people, real traffic.', icon: TrendingUp },
            ].map((item, i) => (
              <div key={i} className="relative">
                <div className="text-8xl font-heading font-extrabold text-white/5 absolute -top-4 -left-2">
                  {item.step}
                </div>
                <div className="relative z-10 pt-8">
                  <div className="w-14 h-14 rounded-xl bg-neon-mint/10 flex items-center justify-center mb-4">
                    <item.icon className="w-7 h-7 text-neon-mint" />
                  </div>
                  <h3 className="font-heading font-semibold text-xl text-white mb-2">{item.title}</h3>
                  <p className="text-gray-400">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8">
            <h2 className="font-heading font-bold text-3xl sm:text-4xl text-white mb-4">
              Simple, Transparent <span className="text-neon-mint">Pricing</span>
            </h2>
            <p className="text-gray-400 mb-8">Start free. Upgrade when you need more power.</p>
            
            {/* Billing Toggle */}
            <div className="inline-flex items-center gap-4 p-2 bg-void-paper rounded-full border border-white/10">
              <span className={`text-sm ${!yearlyBilling ? 'text-white' : 'text-gray-500'}`}>Monthly</span>
              <Switch
                checked={yearlyBilling}
                onCheckedChange={setYearlyBilling}
                className="data-[state=checked]:bg-neon-mint"
              />
              <span className={`text-sm ${yearlyBilling ? 'text-white' : 'text-gray-500'}`}>
                Yearly
                <span className="ml-2 px-2 py-0.5 bg-neon-mint/20 text-neon-mint text-xs rounded-full">
                  2 months free
                </span>
              </span>
            </div>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {/* Free Plan */}
            <div className="bg-void-paper border border-white/10 rounded-xl p-6">
              <div className="mb-6">
                <h3 className="font-heading font-semibold text-lg text-white">Free</h3>
                <p className="text-gray-500 text-sm">Get started</p>
              </div>
              <div className="mb-6">
                <span className="font-heading font-extrabold text-4xl text-white">$0</span>
                <span className="text-gray-500">/month</span>
              </div>
              <ul className="space-y-3 mb-8">
                {['50 starting credits', '1 active promotion', '1 capture page', 'Earn credits by viewing', 'Basic analytics', 'Daily login bonus', 'Referral program'].map((f, i) => (
                  <li key={i} className="flex items-center gap-2 text-gray-400 text-sm">
                    <CheckCircle className="w-4 h-4 text-gray-500" />
                    {f}
                  </li>
                ))}
              </ul>
              <Link to="/auth?mode=register">
                <Button variant="outline" className="w-full border-white/20 text-white hover:bg-white/5">
                  Get Started
                </Button>
              </Link>
            </div>
            
            {/* Pro Plan */}
            <div className="bg-void-paper border-2 border-neon-mint rounded-xl p-6 relative shadow-neon">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-neon-mint text-black text-xs font-bold rounded-full">
                POPULAR
              </div>
              <div className="mb-6">
                <h3 className="font-heading font-semibold text-lg text-white">Pro</h3>
                <p className="text-gray-500 text-sm">For serious marketers</p>
              </div>
              <div className="mb-6">
                <span className="font-heading font-extrabold text-4xl text-white">${getPrice(19)}</span>
                <span className="text-gray-500">/{yearlyBilling ? 'mo' : 'month'}</span>
                {yearlyBilling && (
                  <p className="text-neon-mint text-sm mt-1">Billed ${getPrice(19) * 12}/year</p>
                )}
              </div>
              <p className="text-neon-mint text-xs font-medium mb-3">Everything in Free, plus:</p>
              <ul className="space-y-3 mb-8">
                {['200 bonus credits on upgrade', '100 credits every month', '5 active promotions', '5 capture pages', 'Add leads manually (50/mo)', 'AI content generator', 'Scheduled messages', 'AI optimization tips', 'Email your leads', 'Priority support'].map((f, i) => (
                  <li key={i} className="flex items-center gap-2 text-gray-300 text-sm">
                    <CheckCircle className="w-4 h-4 text-neon-mint" />
                    {f}
                  </li>
                ))}
              </ul>
              <Button 
                className="w-full bg-neon-mint text-black hover:bg-neon-mint/90 font-bold shadow-neon"
                onClick={() => {
                  const token = localStorage.getItem('token');
                  if (token) {
                    navigate('/dashboard/billing');
                  } else {
                    navigate('/auth?mode=register&plan=pro');
                  }
                }}
              >
                Upgrade to Pro
              </Button>
            </div>
            
            {/* Power Plan */}
            <div className="bg-void-paper border border-neon-violet/50 rounded-xl p-6">
              <div className="mb-6">
                <h3 className="font-heading font-semibold text-lg text-white">Power</h3>
                <p className="text-gray-500 text-sm">Maximum growth</p>
              </div>
              <div className="mb-6">
                <span className="font-heading font-extrabold text-4xl text-white">${getPrice(39)}</span>
                <span className="text-gray-500">/{yearlyBilling ? 'mo' : 'month'}</span>
                {yearlyBilling && (
                  <p className="text-neon-violet text-sm mt-1">Billed ${getPrice(39) * 12}/year</p>
                )}
              </div>
              <p className="text-neon-violet text-xs font-medium mb-3">Everything in Pro, plus:</p>
              <ul className="space-y-3 mb-8">
                {['500 bonus credits on upgrade', '300 credits every month', 'Unlimited promotions', '10 capture pages', 'Unlimited manual lead add', 'CSV lead import (5,000/upload)', 'AI image generator', 'Scheduled messages', 'Advanced analytics', 'Dedicated support'].map((f, i) => (
                  <li key={i} className="flex items-center gap-2 text-gray-300 text-sm">
                    <CheckCircle className="w-4 h-4 text-neon-violet" />
                    {f}
                  </li>
                ))}
              </ul>
              <Button 
                variant="outline" 
                className="w-full border-neon-violet text-neon-violet hover:bg-neon-violet/10"
                onClick={() => {
                  const token = localStorage.getItem('token');
                  if (token) {
                    navigate('/dashboard/billing');
                  } else {
                    navigate('/auth?mode=register&plan=power');
                  }
                }}
              >
                Upgrade to Power
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6 bg-void-paper">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-heading font-bold text-3xl sm:text-4xl text-white mb-6">
            Ready to Get More Traffic?
          </h2>
          <p className="text-gray-400 mb-8 max-w-xl mx-auto">
            Join thousands of marketers already growing with TrafficFlow AI. 
            Start earning and promoting today.
          </p>
          <Link to="/auth?mode=register">
            <Button 
              size="lg" 
              className="bg-neon-mint text-black hover:bg-neon-mint/90 font-bold text-lg px-10 shadow-neon-lg btn-press"
            >
              Create Free Account <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 border-t border-white/10">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6 mb-8">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-neon-mint rounded-lg flex items-center justify-center">
                <Zap className="w-5 h-5 text-black" />
              </div>
              <span className="font-heading font-bold text-white">TrafficFlow</span>
              <span className="font-heading font-bold text-neon-violet">AI</span>
            </div>
            <div className="flex flex-wrap justify-center gap-6 text-sm">
              <Link to="/support" className="text-gray-400 hover:text-white transition-colors">Support & FAQ</Link>
              <Link to="/privacy" className="text-gray-400 hover:text-white transition-colors">Privacy Policy</Link>
              <Link to="/terms" className="text-gray-400 hover:text-white transition-colors">Terms of Service</Link>
              <Link to="/cookies" className="text-gray-400 hover:text-white transition-colors">Cookie Settings</Link>
            </div>
          </div>
          <div className="flex flex-col md:flex-row items-center justify-between gap-4 pt-6 border-t border-white/5">
            <p className="text-gray-500 text-sm">
              © 2026 TrafficFlow AI. All rights reserved.
            </p>
            <p className="text-gray-500 text-sm">
              Contact: <a href="mailto:support@trafficflowai.com" className="text-neon-mint hover:underline">support@trafficflowai.com</a>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
